import { FC, MouseEventHandler, PropsWithChildren } from 'react';
import { Flex } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { colorGray } from '@atlas-frontend/design-tokens';
import { DividerHeaderButton } from './DividerHeaderButton';
import { DividerHeaderDeleteButton } from './DividerHeaderDeleteButton';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DividerHeaderButtons.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'header-divider-buttons',
);
addTranslations(translations);

export interface DividerHeaderButtonsProps extends PropsWithChildren {
  id: string;
  onAddButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onEditButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onCloneButtonClick?: MouseEventHandler<HTMLButtonElement>;
  onDeleteButtonClick?: MouseEventHandler<HTMLElement>;
  onEditGraphClick?: MouseEventHandler<HTMLElement>;
  editGraphLabel?: string;
  hasError?: boolean;
  disabled?: boolean;
  disabledLabel?: string;
  isLoading?: 'add' | 'manage' | 'clone' | 'delete';
}

export const DividerHeaderButtons: FC<DividerHeaderButtonsProps> = (props) => {
  const {
    id,
    children,
    onAddButtonClick,
    onEditButtonClick,
    onCloneButtonClick,
    onDeleteButtonClick,
    onEditGraphClick,
    editGraphLabel,
    hasError,
    disabled = false,
    disabledLabel = '',
    isLoading,
  } = props;
  const { t } = useTranslation();

  const showActions =
    onAddButtonClick ||
    onEditButtonClick ||
    onCloneButtonClick ||
    onEditGraphClick ||
    onDeleteButtonClick;

  return (
    <Header justify={showActions ? 'space-between' : 'start'}>
      {children}
      {showActions && (
        <Flex gap="small">
          <ElementWrapper gap="middle">
            <DividerHeaderButton
              id={`${id}-add`}
              label={disabled ? disabledLabel : t('button.add')}
              icon={<Icon.Plus size={18} />}
              onClick={onAddButtonClick}
              hasError={hasError}
              disabled={disabled}
              loading={isLoading === 'add'}
            />
            <DividerHeaderButton
              id={`${id}-manage`}
              label={
                disabled
                  ? disabledLabel
                  : editGraphLabel || t('button.manage-graph')
              }
              icon={<Icon.GitPullRequest size={18} />}
              onClick={onEditGraphClick}
              hasError={hasError}
              disabled={disabled}
              loading={isLoading === 'manage'}
            />
            <DividerHeaderButton
              id={`${id}-manage`}
              label={disabled ? disabledLabel : t('button.manage')}
              icon={<Icon.Edit3 size={18} />}
              onClick={onEditButtonClick}
              hasError={hasError}
              disabled={disabled}
              loading={isLoading === 'manage'}
            />
            <DividerHeaderButton
              id={`${id}-clone`}
              label={disabled ? disabledLabel : t('button.clone')}
              icon={<Icon.Copy size={18} />}
              onClick={onCloneButtonClick}
              hasError={hasError}
              disabled={disabled}
              loading={isLoading === 'clone'}
            />
            <DividerHeaderDeleteButton
              id={id}
              label={disabled ? disabledLabel : t('button.delete')}
              onClick={onDeleteButtonClick}
              hasError={hasError}
              disabled={disabled}
              loading={isLoading === 'delete'}
            />
          </ElementWrapper>
        </Flex>
      )}
    </Header>
  );
};

const Header = styled(Flex)`
  position: relative;

  &:after {
    position: absolute;
    top: 50%;
    left: 0;
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background-color: ${colorGray[3]};
  }
`;

const ElementWrapper = styled(Flex)`
  position: relative;
  z-index: 1;
  background-color: white;
  padding-inline: 16px;
  margin-inline: 20px;
`;
